import React from 'react';
import { StatusCodes } from 'http-status-codes';
import { Stack, Constrain } from '@moda/om';
import { useFeatureFlag } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { PaymentSource } from '../../constants';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { useUser } from '../../hooks/useUser';
import { useCart, useCartItems } from '../../hooks/useCart';
import { CheckoutGiftCardSection } from '../../components/CheckoutGiftCardSection';
import { useAddressesQuery } from '../../hooks/useAddress';
import { usePaymentMethodsQuery } from '../../hooks/usePaymentMethods';
import { useSanitizePaymentSource } from '../../hooks/useCheckout/useSanitizePaymentSource';
import { useCheckout, useCheckoutStartTracking } from '../../hooks/useCheckout';
import { CheckoutFormStatesProvider } from '../../components/CheckoutFormStates';
import { RedirectWithStatusCode } from '../../components/RedirectWithStatusCode';
import { AccountEmailInUseLink } from '../../components/AccountEmailInUseLink';
import { Loading } from '../../components/Loading';
import { PageMeta } from '../../components/PageMeta';
import { CheckoutAuthentication } from '../../components/Authentication';
import { CheckoutSection } from '../../components/CheckoutSection';
import { LineItemList } from '../../components/LineItemList';
import { CheckoutHeader } from '../../components/CheckoutHeader';
import { CheckoutPaymentSummary } from '../../components/PaymentSummary';
import { ShippingMethod } from '../../components/ShippingMethod';
import { CheckoutPaymentMethod } from '../../components/PaymentMethod';
import { CheckoutShippingAddress } from '../../components/ShippingAddress';
import { CheckoutAmazonShippingAndPayment } from '../../components/AmazonShippingAndPayment';
import { CheckoutPayPalShippingAndPayment } from '../../components/PayPalShippingAndPayment';
import { CheckoutPaymentSources } from '../../components/CheckoutPaymentSources';
import { CheckoutSiteCredit } from '../../components/CheckoutSiteCredit';
import { CheckoutGiftSection } from '../../components/CheckoutGiftSection';
import { CheckoutGiftSectionError } from '../../components/CheckoutGiftSection/CheckoutGiftSectionError';
import { CheckoutNotifications } from '../../components/CheckoutNotifications';

import './CheckoutPage.scss';

export const CheckoutPage: React.FC = () => {
  const {
    user: { accessToken }
  } = useUser();
  const { cart, loading: loadingCart, loaded: cartLoaded, error: cartError } = useCart();
  const { addresses, loading: loadingAddresses, error: addressesError } = useAddressesQuery();
  const {
    attributes: { priceChangedMessage }
  } = cart;

  const {
    data: paymentMethods,
    loading: loadingPaymentMethods,
    error: paymentMethodsError
  } = usePaymentMethodsQuery();
  const { availableNowItems, preorderItems, unavailableItems } = useCartItems();
  const allItems = [...availableNowItems, ...preorderItems, ...unavailableItems];
  const hasExcludeSameDayItems = allItems.some(item => item.attributes.excludeSameDayMessage);
  const sameDayDeliveryMessage = hasExcludeSameDayItems
    ? 'Same-day delivery is available in your area.\nTo select Same-day shipping, your order must contain only eligible items.\nPlease move any items labeled “Not eligible for expedited shipping” or “Preorder” to “Save for Later” to continue.'
    : undefined;
  const {
    isShippingAddressSelected,
    isShippingMethodSelected,
    isPaymentMethodSelected,
    canPlaceOrder
  } = useCheckout();

  useCheckoutStartTracking();

  const { loading: loadingPaymentSource } = useSanitizePaymentSource();

  const allowGiftCard = useFeatureFlag('allow-gift-card', [
    { default: false },
    { if: true, then: true }
  ]);

  if (loadingCart || loadingAddresses || loadingPaymentMethods || loadingPaymentSource)
    return <Loading />;

  if (accessToken && (!addresses || !paymentMethods)) return <Loading />;

  if (cartLoaded && !cart.attributes.cartItemsCount)
    return (
      <RedirectWithStatusCode
        statusCode={StatusCodes.MOVED_TEMPORARILY}
        to={hrefFor.ShoppingBagPage()}
      />
    );

  if (cartError) throwQueryError(cartError);
  if (addressesError) throwQueryError(addressesError);
  if (paymentMethodsError) throwQueryError(paymentMethodsError);

  return (
    <CheckoutFormStatesProvider>
      <PageMeta title="Checkout" />

      <div className="CheckoutPage">
        <div>
          <CheckoutHeader />

          {isShippingAddressSelected &&
            isShippingMethodSelected &&
            isPaymentMethodSelected &&
            !canPlaceOrder &&
            accessToken && (
              <div className="CheckoutPage__error">
                <Constrain>Please resolve the errors below before proceeding.</Constrain>
              </div>
            )}

          <CheckoutGiftSectionError />
        </div>

        {!accessToken && (
          <Constrain>
            <CheckoutAuthentication />
          </Constrain>
        )}

        {accessToken && (
          <Constrain>
            <Stack space={4}>
              <AccountEmailInUseLink />
              <CheckoutPaymentSources />
              <div className="CheckoutPage__main">
                <div className="CheckoutPage__form" id="checkout-form">
                  <CheckoutNotifications priceChangedMessage={priceChangedMessage} />
                  {(cart.attributes.paymentSource === PaymentSource.Braintree ||
                    cart.attributes.paymentSource === PaymentSource.BraintreeAffirm ||
                    cart.attributes.paymentSource === PaymentSource.Moda) && (
                    <>
                      <CheckoutShippingAddress />
                      {isShippingAddressSelected && availableNowItems.length > 0 && (
                        <ShippingMethod />
                      )}

                      <CheckoutGiftSection />

                      {isShippingAddressSelected && isShippingMethodSelected && (
                        <>
                          <CheckoutPaymentMethod />
                          {allowGiftCard && <CheckoutGiftCardSection />}
                        </>
                      )}
                    </>
                  )}

                  {cart.attributes.paymentSource === PaymentSource.Amazon && (
                    <>
                      <CheckoutAmazonShippingAndPayment />
                      {isShippingAddressSelected &&
                        isPaymentMethodSelected &&
                        availableNowItems.length > 0 && <ShippingMethod />}

                      <CheckoutGiftSection />
                    </>
                  )}

                  {cart.attributes.paymentSource === PaymentSource.PayPal && (
                    <>
                      <CheckoutPayPalShippingAndPayment />
                      {isShippingAddressSelected &&
                        isPaymentMethodSelected &&
                        availableNowItems.length > 0 && <ShippingMethod />}

                      <CheckoutGiftSection />
                    </>
                  )}

                  {isShippingAddressSelected &&
                    isShippingMethodSelected &&
                    isPaymentMethodSelected && (
                      <>
                        <CheckoutSiteCredit />
                        <CheckoutSection title="Review Order Items" divider={false}>
                          <Stack space={4}>
                            {availableNowItems.length > 0 && (
                              <LineItemList
                                title="Available Now"
                                theme="simple"
                                items={availableNowItems}
                                showPriceWithVAT
                              />
                            )}

                            {preorderItems.length > 0 && (
                              <LineItemList
                                title="Preorder"
                                subtitle={sameDayDeliveryMessage}
                                theme="simple"
                                items={preorderItems}
                                showPriceWithVAT
                              />
                            )}

                            {unavailableItems.length > 0 && (
                              <LineItemList
                                title="Unavailable"
                                theme="simple"
                                items={unavailableItems}
                                showPriceWithVAT
                              />
                            )}
                          </Stack>
                        </CheckoutSection>
                      </>
                    )}
                </div>
                <div className="CheckoutPage__summary">
                  <CheckoutPaymentSummary />
                </div>
              </div>
            </Stack>
          </Constrain>
        )}
      </div>
    </CheckoutFormStatesProvider>
  );
};
